import { action, computed, makeObservable, observable } from "mobx";

import type {
  ScorecardAllRowsListState,
  ScorecardCriterionRowsListState,
  ScorecardViewState,
} from "../components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/types";
import { hasComputedCriterionSummary } from "../components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/utils";

import type {
  ConfigureScorecardRequest,
  CriterionSummary,
  GetScorecardResponse,
  Scorecard,
  ScorecardRowSummary,
  ScorecardStep,
} from "@/queries/smart-tables/scorecard";
import { SCORECARD_DEFAULT_THRESHOLDS, ScorecardStatus } from "@/queries/smart-tables/scorecard";

export interface RemovedCriterionSnapshot {
  stepId: string;
  title: string;
  primitiveType: string;
  backingColumnId: string | null;
  summary?: CriterionSummary;
}

export interface ScorecardDisplayCriterion {
  stepId: string;
  step?: ScorecardStep;
  summary?: CriterionSummary;
  isRemoved: boolean;
  primitiveType?: string;
  title?: string;
}

export type ScorecardRecalculateTarget =
  | { scope: "all" }
  | { scope: "row"; rowIndex: number }
  | { scope: "criterion"; stepId: string }
  | { scope: "steps"; stepIds: string[] };

export type ScorecardRecalculatePhase = "idle" | "queueing" | "running";

const MIN_RECALCULATE_FEEDBACK_MS = 900;

export class ScorecardStore {
  view: ScorecardViewState = { view: "overview" };

  removedCriteria: RemovedCriterionSnapshot[] = [];
  /** Optimistic scorecard config while configure mutation is in flight. */
  pendingScorecard: Scorecard | null = null;
  /** Cached row summaries so list views stay populated between refetches. */
  cachedRows: ScorecardRowSummary[] = [];
  criterionSummaries: CriterionSummary[] = [];
  allRowsListState: ScorecardAllRowsListState = { search: "", filter: "all" };
  criterionRowsListState: ScorecardCriterionRowsListState = { search: "", filter: "all" };
  recalculateTarget: ScorecardRecalculateTarget | null = null;
  recalculatePhase: ScorecardRecalculatePhase = "idle";
  recalculateStartedAt: number | null = null;
  private recalculateRunId = 0;
  private recalculateCompletionTimeout: number | null = null;
  lastCompletedCalculationId: string | null = null;

  constructor() {
    makeObservable(this, {
      view: observable,

      removedCriteria: observable,
      pendingScorecard: observable,
      cachedRows: observable,
      criterionSummaries: observable,
      allRowsListState: observable,
      criterionRowsListState: observable,
      recalculateTarget: observable,
      recalculatePhase: observable,
      recalculateStartedAt: observable,
      lastCompletedCalculationId: observable,
      displayScorecard: computed,
      displayCriteria: computed,
      displayRows: computed,
      isRecalculateActive: computed,
      setView: action,
      setAllRowsListState: action,
      setCriterionRowsListState: action,
      navigateOverview: action,

      syncFromServer: action,
      syncRows: action,
      syncSummaries: action,
      beginConfigure: action,
      finishConfigure: action,
      clearRemovedCriteria: action,
      beginRecalculate: action,
      markRecalculateRunning: action,
      clearRecalculate: action,
      handleRecalculationCompleted: action,
    });
  }

  get displayScorecard(): Scorecard | null {
    return this.pendingScorecard;
  }

  get displayRows(): ScorecardRowSummary[] {
    return this.cachedRows;
  }

  get isRecalculateActive(): boolean {
    return this.recalculatePhase !== "idle";
  }

  get displayCriteria(): ScorecardDisplayCriterion[] {
    const scorecard = this.pendingScorecard;
    if (!scorecard) {
      return [];
    }

    const activeStepIds = new Set(scorecard.steps.map(step => step.id));
    if (activeStepIds.size === 0) {
      return [];
    }

    const active: ScorecardDisplayCriterion[] = scorecard.steps.map(step => ({
      stepId: step.id,
      step,
      summary: this.criterionSummaries.find(summary => summary.step_id === step.id),
      isRemoved: false,
      primitiveType: step.primitive_type,
      title: step.title,
    }));

    const removed = this.removedCriteria
      .filter(snapshot => !activeStepIds.has(snapshot.stepId))
      .map(snapshot => ({
        stepId: snapshot.stepId,
        step: undefined,
        summary: snapshot.summary,
        isRemoved: true,
        primitiveType: snapshot.primitiveType,
        title: snapshot.title,
      }));

    const removedStepIds = new Set(removed.map(item => item.stepId));
    const historicalRemoved: ScorecardDisplayCriterion[] = this.criterionSummaries
      .filter(
        summary =>
          !activeStepIds.has(summary.step_id) &&
          !removedStepIds.has(summary.step_id) &&
          hasComputedCriterionSummary(summary),
      )
      .map(summary => ({
        stepId: summary.step_id,
        step: undefined,
        summary,
        isRemoved: true,
        title: summary.title,
      }));

    return [...active, ...removed, ...historicalRemoved];
  }

  setView = (next: ScorecardViewState): void => {
    this.view = next;
  };

  setAllRowsListState = (next: Partial<ScorecardAllRowsListState>): void => {
    this.allRowsListState = { ...this.allRowsListState, ...next };
  };

  setCriterionRowsListState = (next: Partial<ScorecardCriterionRowsListState>): void => {
    this.criterionRowsListState = { ...this.criterionRowsListState, ...next };
  };

  navigateOverview = (): void => {
    this.view = { view: "overview" };
  };

  syncFromServer = (scorecardData?: GetScorecardResponse | null): void => {
    const scorecard = scorecardData?.scorecard ?? null;
    const latestCalculation = scorecardData?.latest_calculation;
    const displayCalculation = scorecardData?.display_calculation;
    if (latestCalculation?.status === ScorecardStatus.COMPLETED && latestCalculation.id) {
      this.lastCompletedCalculationId = latestCalculation.id;
    } else if (displayCalculation?.status === ScorecardStatus.COMPLETED && displayCalculation.id) {
      this.lastCompletedCalculationId = displayCalculation.id;
    }

    if (!scorecard) {
      if (this.view.view !== "overview" && this.view.view !== "add-criterion") {
        this.view = { view: "overview" };
      }
      this.pendingScorecard = null;
      this.lastCompletedCalculationId = null;
      return;
    }

    this.pendingScorecard = scorecard;

    const activeStepIds = new Set(scorecard.steps.map(step => step.id));
    if (activeStepIds.size === 0) {
      this.clearRemovedCriteria();
    } else {
      this.removedCriteria = this.removedCriteria.filter(
        snapshot => !activeStepIds.has(snapshot.stepId),
      );
    }

    const { status } = scorecard;
    if (status === ScorecardStatus.COMPLETED && !scorecard.stale_state?.is_stale) {
      this.clearRemovedCriteria();
    }
  };

  syncRows = (rows: ScorecardRowSummary[]): void => {
    if (rows.length > 0) {
      this.cachedRows = rows;
    }
  };

  syncSummaries = (
    summaries: CriterionSummary[],
    options?: { preserveExistingWhenEmpty?: boolean; mergeWithExisting?: boolean },
  ): void => {
    if (options?.mergeWithExisting) {
      if (summaries.length === 0 && options.preserveExistingWhenEmpty) {
        return;
      }

      const nextSummariesByStepId = new Map(
        this.criterionSummaries.map(summary => [summary.step_id, summary]),
      );
      summaries.forEach(summary => {
        if (hasComputedCriterionSummary(summary)) {
          nextSummariesByStepId.set(summary.step_id, summary);
        }
      });
      this.criterionSummaries = Array.from(nextSummariesByStepId.values());
      return;
    }

    if (summaries.length > 0 || !options?.preserveExistingWhenEmpty) {
      this.criterionSummaries = summaries;
    }
  };

  beginConfigure = (
    currentScorecard: Scorecard,
    payload: ConfigureScorecardRequest,
    summaries: CriterionSummary[],
  ): { removedBackingColumnIds: string[] } => {
    const nextStepIds = new Set(
      payload.steps.map(step => step.id).filter((id): id is string => Boolean(id)),
    );
    const removedSteps = currentScorecard.steps.filter(step => !nextStepIds.has(step.id));

    if (payload.steps.length === 0) {
      this.clearRemovedCriteria();
    }

    for (const step of removedSteps) {
      const stepSummary = summaries.find(summary => summary.step_id === step.id);
      if (!hasComputedCriterionSummary(stepSummary)) {
        continue;
      }

      const existing = this.removedCriteria.find(snapshot => snapshot.stepId === step.id);
      if (!existing) {
        this.removedCriteria.push({
          stepId: step.id,
          title: step.title,
          primitiveType: step.primitive_type,
          backingColumnId: step.backing_column_id ?? null,
          summary: stepSummary,
        });
      }
    }

    const mergedSteps: ScorecardStep[] = payload.steps.map((stepInput, index) => {
      const existingStep = stepInput.id
        ? currentScorecard.steps.find(step => step.id === stepInput.id)
        : undefined;
      return {
        id: stepInput.id ?? `pending-${index}`,
        scorecard_id: currentScorecard.id,
        title: stepInput.title,
        description: stepInput.description ?? null,
        primitive_type: stepInput.primitive_type,
        target_column_id: stepInput.target_column_id ?? null,
        source_column_ids: stepInput.source_column_ids ?? [],
        weight: stepInput.weight ?? 1,
        required: stepInput.required ?? true,
        thresholds: stepInput.thresholds ?? SCORECARD_DEFAULT_THRESHOLDS,
        score_adapter: stepInput.score_adapter ?? existingStep?.score_adapter ?? {},
        evidence_mode: stepInput.evidence_mode ?? existingStep?.evidence_mode ?? null,
        order_index: stepInput.order_index ?? index,
        backing_column_id: existingStep?.backing_column_id ?? null,
        config_hash: existingStep?.config_hash ?? "",
        primitive_config: stepInput.primitive_config,
      };
    });

    this.pendingScorecard = {
      ...currentScorecard,
      name: payload.name,
      evaluated_column_ids: payload.evaluated_column_ids,
      aggregation: payload.aggregation,
      steps: mergedSteps,
      status: ScorecardStatus.STALE,
      stale_state: { is_stale: true, reasons: ["scorecard_config_changed"] },
    };

    const removedBackingColumnIds = removedSteps
      .map(step => step.backing_column_id)
      .filter((columnId): columnId is string => Boolean(columnId));

    return { removedBackingColumnIds };
  };

  finishConfigure = (scorecard: Scorecard): void => {
    this.pendingScorecard = scorecard;
    this.view = { view: "overview" };
  };

  clearRemovedCriteria = (): void => {
    this.removedCriteria = [];
  };

  beginRecalculate = (target: ScorecardRecalculateTarget): void => {
    if (this.recalculateCompletionTimeout) {
      window.clearTimeout(this.recalculateCompletionTimeout);
      this.recalculateCompletionTimeout = null;
    }
    this.recalculateRunId += 1;
    this.recalculateTarget = target;
    this.recalculatePhase = "queueing";
    this.recalculateStartedAt = Date.now();
  };

  markRecalculateRunning = (): void => {
    if (this.recalculateTarget) {
      this.recalculatePhase = "running";
    }
  };

  clearRecalculate = (): void => {
    if (this.recalculateCompletionTimeout) {
      window.clearTimeout(this.recalculateCompletionTimeout);
      this.recalculateCompletionTimeout = null;
    }
    this.recalculateTarget = null;
    this.recalculatePhase = "idle";
    this.recalculateStartedAt = null;
  };

  handleRecalculationCompleted = (): void => {
    this.clearRemovedCriteria();

    if (!this.recalculateTarget || this.recalculateStartedAt === null) {
      this.clearRecalculate();
      return;
    }

    const remainingMs = MIN_RECALCULATE_FEEDBACK_MS - (Date.now() - this.recalculateStartedAt);
    if (remainingMs <= 0) {
      this.clearRecalculate();
      return;
    }

    const runId = this.recalculateRunId;
    if (this.recalculateCompletionTimeout) {
      window.clearTimeout(this.recalculateCompletionTimeout);
    }
    this.recalculateCompletionTimeout = window.setTimeout(() => {
      if (this.recalculateRunId === runId) {
        this.clearRecalculate();
      }
    }, remainingMs);
  };
}
